<template>
	<el-menu
		mode="horizontal"
		menu-trigger="hover"
		:default-active="defaultActive"
		class="surf-nav-menu"
		@select="handleSelect"
	>
		<NavMenuItemRenderer
			v-for="menu in processedMenus"
			:key="menu.uuid"
			:menu="menu"
			:index="menu.uuid"
			@click="handleClick"
		/>
	</el-menu>
</template>

<script>
export default {
	name: 'SurfNavMenu'
};
</script>
<script setup>
import { computed } from 'vue';
import { isOverviewMenu, useNavMenu } from './useNavMenu';
import NavMenuItemRenderer from './NavMenuItemRenderer.vue';
import { deepClone } from '@/utils/api';

const props = defineProps({
	menus: {
		type: Array,
		required: true,
		default: () => []
	}
});

const emit = defineEmits(['click']);

const { defaultActive, handleSelect } = useNavMenu();

/**
 * 处理菜单数据：过滤概览菜单的子菜单数据
 */
const processMenu = menu => {
	if (!menu) return null;

	// 如果是概览菜单，不需要展开显示子菜单
	if (isOverviewMenu(menu)) {
		return {
			...menu,
			pageMenuVOS: undefined,
			moduleVOS: undefined
		};
	}

	// 递归处理子菜单
	if (menu.pageMenuVOS && Array.isArray(menu.pageMenuVOS)) {
		return {
			...menu,
			pageMenuVOS: menu.pageMenuVOS.map(processMenu).filter(Boolean)
		};
	}

	return menu;
};

/**
 * 处理后的菜单列表
 */
const processedMenus = computed(() => {
	const copiedMenus = deepClone(props.menus);
	return copiedMenus.map(processMenu).filter(Boolean);
});

/**
 * 处理点击事件
 * 接收来自 NavMenuItemRenderer 的点击事件，并传递给父组件
 */
const handleClick = event => {
	emit('click', event);
};
</script>

<style lang="less" scoped>
// ============================================
// 最外层菜单样式
// ============================================

@surf-nav-menu-slide-width: 52px;

.surf-nav-menu {
	height: 100%;
	border-bottom: none;
	background: transparent;

	// 容器基础样式：重置 Element UI Menu 的默认样式
	&.el-menu {
		border-bottom: none;
		background: transparent;

		&::after,
		&::before {
			display: none;
		}
	}

	// ============================================
	// 一级菜单项样式
	// ============================================
	&.el-menu.el-menu--horizontal {
		/deep/ .el-submenu {
			height: 100%;
			padding: 0 20px;
		}

		/deep/ .el-menu-item {
			height: 100%;
			padding: 0 20px;
			margin: 0;
			line-height: 24px;
			color: rgba(var(--surf-inverse-color-rgb), 0.6);
			font-size: 16px;
			position: relative;
			display: flex;
			align-items: center;
			border-radius: 6px;
			border-bottom: none;
			cursor: pointer;
			transition: color 0.3s ease, font-weight 0.3s ease;
			z-index: 1;
			white-space: nowrap;
			background: transparent;

			// 底部下划线（激活状态）
			&::after {
				content: '';
				position: absolute;
				bottom: 0;
				left: 0;
				right: 0;
				margin: auto;
				width: 0;
				height: 4px;
				background: var(--surf-inverse-color);
				transition: width 0.3s ease;
			}

			&:hover {
				color: var(--surf-inverse-color);
				font-weight: 500;
				background: transparent;
				border-bottom: none;
			}

			&.is-active {
				color: var(--surf-inverse-color);
				font-weight: 500;
				background: transparent;
				border-bottom: none;

				&::after {
					width: calc(100% - @surf-nav-menu-slide-width);
				}
			}

			&:focus {
				background: transparent;
			}
		}
	}

	// ============================================
	// 一级子菜单标题样式
	// ============================================
	&.el-menu.el-menu--horizontal {
		/deep/ .el-submenu {
			position: relative;

			// 底部下划线（激活状态）
			&::after {
				content: '';
				position: absolute;
				bottom: 0;
				left: 0;
				right: 0;
				margin: auto;
				width: 0;
				height: 4px;
				background: var(--surf-inverse-color);
				transition: width 0.3s ease;
			}

			&.is-active {
				background: transparent;
				border-bottom: none;

				// 只应用在直接子元素，避免影响到向下的菜单层级展示
				> .el-submenu__title {
					color: var(--surf-inverse-color);
					font-weight: 500;
				}

				&::after {
					width: calc(100% - @surf-nav-menu-slide-width);
				}
			}

			.el-submenu__title {
				height: 100%;
				padding: 0;
				margin: 0;
				line-height: 24px;
				color: rgba(var(--surf-inverse-color-rgb), 0.6);
				font-size: 16px;
				position: relative;
				display: flex;
				align-items: center;
				justify-content: space-between;
				border-radius: 6px;
				border-bottom: none;
				cursor: pointer;
				transition: all 0.3s ease;
				z-index: 1;
				white-space: nowrap;
				background: transparent;

				// 隐藏 Element UI 默认箭头（必须使用 !important 覆盖内联样式）
				.el-submenu__icon-arrow {
					display: none !important;
				}

				&:hover {
					color: var(--surf-inverse-color);
					font-weight: 500;
					background: transparent;
					border-bottom: none;
				}

				&:focus {
					background: transparent;
				}
			}
		}
	}
}
</style>
